import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { SkillCheckFilters, SkillCheckJobStatus } from "../types/admin.types";

/**
 * フィルタ状態の型定義
 */
export type FilterState = {
  status: SkillCheckJobStatus | "";
  matchLevel: number;
  query: string;
  teamId: string;
  companyId: string;
  jobPostingId: string;
  since: string;
  until: string;
  sortOrder: "asc" | "desc";
  page: number;
};

/**
 * フィルタのデフォルト値
 */
export const DEFAULT_FILTER_STATE: FilterState = {
  status: "",
  matchLevel: -1,
  query: "",
  teamId: "",
  companyId: "",
  jobPostingId: "",
  since: "",
  until: "",
  sortOrder: "desc",
  page: 0,
};

/**
 * URLクエリパラメータのキー名
 */
const URL_PARAM_KEYS = {
  status: "status",
  matchLevel: "level",
  query: "q",
  teamId: "team",
  companyId: "company",
  jobPostingId: "job",
  since: "since",
  until: "until",
  sortOrder: "sort",
  page: "page",
} as const;

/**
 * 有効なステータス値かどうかを判定
 */
function isValidStatus(value: string): value is SkillCheckJobStatus {
  return ["PENDING", "PROCESSING", "COMPLETED", "FAILED", "CANCELLED", "RETRYABLE"].includes(value);
}

/**
 * URLクエリパラメータからフィルタ状態を復元
 */
function parseSearchParams(searchParams: URLSearchParams): FilterState {
  const statusParam = searchParams.get(URL_PARAM_KEYS.status);
  const levelParam = searchParams.get(URL_PARAM_KEYS.matchLevel);
  const sortParam = searchParams.get(URL_PARAM_KEYS.sortOrder);
  const pageParam = searchParams.get(URL_PARAM_KEYS.page);

  return {
    status: statusParam && isValidStatus(statusParam) ? statusParam : "",
    matchLevel: levelParam ? Number.parseInt(levelParam, 10) : -1,
    query: searchParams.get(URL_PARAM_KEYS.query) ?? "",
    teamId: searchParams.get(URL_PARAM_KEYS.teamId) ?? "",
    companyId: searchParams.get(URL_PARAM_KEYS.companyId) ?? "",
    jobPostingId: searchParams.get(URL_PARAM_KEYS.jobPostingId) ?? "",
    since: searchParams.get(URL_PARAM_KEYS.since) ?? "",
    until: searchParams.get(URL_PARAM_KEYS.until) ?? "",
    sortOrder: sortParam === "asc" ? "asc" : "desc",
    page: pageParam ? Math.max(0, Number.parseInt(pageParam, 10)) : 0,
  };
}

/**
 * フィルタ状態をURLクエリパラメータに変換
 */
function buildSearchParams(state: FilterState): URLSearchParams {
  const params = new URLSearchParams();

  if (state.status) {
    params.set(URL_PARAM_KEYS.status, state.status);
  }
  if (state.matchLevel >= 0) {
    params.set(URL_PARAM_KEYS.matchLevel, state.matchLevel.toString());
  }
  if (state.query) {
    params.set(URL_PARAM_KEYS.query, state.query);
  }
  if (state.teamId) {
    params.set(URL_PARAM_KEYS.teamId, state.teamId);
  }
  if (state.companyId) {
    params.set(URL_PARAM_KEYS.companyId, state.companyId);
  }
  if (state.jobPostingId) {
    params.set(URL_PARAM_KEYS.jobPostingId, state.jobPostingId);
  }
  if (state.since) {
    params.set(URL_PARAM_KEYS.since, state.since);
  }
  if (state.until) {
    params.set(URL_PARAM_KEYS.until, state.until);
  }
  if (state.sortOrder !== "desc") {
    params.set(URL_PARAM_KEYS.sortOrder, state.sortOrder);
  }
  if (state.page > 0) {
    params.set(URL_PARAM_KEYS.page, state.page.toString());
  }

  return params;
}

/**
 * フィルタ状態をAPI用のフィルタオブジェクトに変換
 */
function buildApiFilters(state: FilterState): SkillCheckFilters {
  const filters: SkillCheckFilters = {
    sortOrder: state.sortOrder,
  };

  if (state.status) {
    filters.status = state.status;
  }
  if (state.matchLevel >= 0) {
    filters.matchLevel = state.matchLevel;
  }
  if (state.query) {
    filters.query = state.query;
  }
  if (state.teamId) {
    filters.teamId = state.teamId;
  }
  if (state.companyId) {
    filters.companyId = state.companyId;
  }
  if (state.jobPostingId) {
    filters.jobPostingId = state.jobPostingId;
  }
  if (state.since) {
    filters.since = new Date(state.since).toISOString();
  }
  if (state.until) {
    filters.until = new Date(`${state.until}T23:59:59`).toISOString();
  }

  return filters;
}

/**
 * スキルチェック一覧のフィルタ状態をURLクエリパラメータと同期するカスタムフック
 *
 * 機能:
 * - URLクエリパラメータからフィルタ状態を復元
 * - フィルタ変更時にURLを更新
 * - ブラウザの戻る/進むに対応
 * - フィルタ状態をAPI用のオブジェクトに変換
 */
export function useSkillCheckFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  // URLパラメータからフィルタ状態を復元
  const filterState = useMemo(() => parseSearchParams(searchParams), [searchParams]);

  // 検索入力のデバウンス用ローカル状態
  const [searchInput, setSearchInput] = useState(filterState.query);

  /**
   * フィルタを更新（useEffectより先に定義する必要がある）
   */
  const updateFilters = useCallback(
    (updates: Partial<FilterState>) => {
      const newState = { ...filterState, ...updates };
      const params = buildSearchParams(newState);
      setSearchParams(params, { replace: false });
    },
    [filterState, setSearchParams],
  );

  // 検索入力のデバウンス
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filterState.query) {
        updateFilters({ query: searchInput, page: 0 });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, filterState.query, updateFilters]);

  // URLパラメータが変更されたときに検索入力を同期
  useEffect(() => {
    setSearchInput(filterState.query);
  }, [filterState.query]);

  /**
   * 単一のフィルタを更新（ページをリセット）
   */
  const setStatus = useCallback(
    (status: SkillCheckJobStatus | "") => {
      updateFilters({ status, page: 0 });
    },
    [updateFilters],
  );

  const setMatchLevel = useCallback(
    (matchLevel: number) => {
      updateFilters({ matchLevel, page: 0 });
    },
    [updateFilters],
  );

  const setTeamId = useCallback(
    (teamId: string) => {
      updateFilters({ teamId, page: 0 });
    },
    [updateFilters],
  );

  const setCompanyId = useCallback(
    (companyId: string) => {
      updateFilters({ companyId, page: 0 });
    },
    [updateFilters],
  );

  const setJobPostingId = useCallback(
    (jobPostingId: string) => {
      updateFilters({ jobPostingId, page: 0 });
    },
    [updateFilters],
  );

  const setSince = useCallback(
    (since: string) => {
      updateFilters({ since, page: 0 });
    },
    [updateFilters],
  );

  const setUntil = useCallback(
    (until: string) => {
      updateFilters({ until, page: 0 });
    },
    [updateFilters],
  );

  const setSortOrder = useCallback(
    (sortOrder: "asc" | "desc") => {
      updateFilters({ sortOrder, page: 0 });
    },
    [updateFilters],
  );

  const setPage = useCallback(
    (page: number) => {
      updateFilters({ page });
    },
    [updateFilters],
  );

  /**
   * 全フィルタをリセット
   */
  const resetFilters = useCallback(() => {
    setSearchInput("");
    setSearchParams(new URLSearchParams(), { replace: false });
  }, [setSearchParams]);

  /**
   * アクティブなフィルタの数を計算
   */
  const activeFilterCount = useMemo(
    () =>
      [
        filterState.status,
        filterState.matchLevel >= 0 ? filterState.matchLevel : null,
        filterState.query,
        filterState.teamId,
        filterState.companyId,
        filterState.jobPostingId,
        filterState.since,
        filterState.until,
      ].filter(Boolean).length,
    [filterState],
  );

  /**
   * API用のフィルタオブジェクトを生成
   */
  const apiFilters = useMemo(() => buildApiFilters(filterState), [filterState]);

  /**
   * 現在のURLをシェア用に取得
   */
  const getShareableUrl = useCallback(() => window.location.href, []);

  return {
    // フィルタ状態
    filters: filterState,
    apiFilters,

    // 検索入力（デバウンス前）
    searchInput,
    setSearchInput,

    // セッター関数
    setStatus,
    setMatchLevel,
    setTeamId,
    setCompanyId,
    setJobPostingId,
    setSince,
    setUntil,
    setSortOrder,
    setPage,

    // ユーティリティ
    resetFilters,
    activeFilterCount,
    getShareableUrl,
    updateFilters,
  };
}
